import * as React from 'react';
import * as formatParse from 'common/format-parse';
import { observer } from 'mobx-react';
import { StockAmount as StockAmountBusinessLogic } from 'nomst/Stores/Decision/stockAmount';
import { Configuration } from 'nomst/Stores/Configuration/configuration';
import { Input, FormGroup } from '@payscale/design';
import { ICountryDecisionDispatcher } from 'typings/countryDecision.dispatcher';
import { AmountCode } from 'typings/enums';
import EligibilityTooltip from 'components/Common/eligibilityTooltip';
import './amount.scss';

export interface IStockAmountProps {
    employeeSourceId: string;
    stockAmount: StockAmountBusinessLogic;
    configuration: Configuration;
    countryDecisionDispatcher: Nullable<ICountryDecisionDispatcher>;
    isReadOnly: boolean;
}

export interface IStockAmountState {
    amountText: string;
    numberOfShareText: string;
    isAmountOnFocus: boolean;
    isNumberOfShareOnFocus: boolean;
    amountValueBeingUpdated: boolean;
}

export class StockAmount extends React.Component<IStockAmountProps, IStockAmountState> {

    constructor(props: IStockAmountProps) {
        super(props);
        this.state = {
            amountText: '',
            numberOfShareText: '',
            isAmountOnFocus: false,
            isNumberOfShareOnFocus: false,
            amountValueBeingUpdated: false
        };

        this.onChangeNumberOfShare = this.onChangeNumberOfShare.bind(this);
        this.onBlurNumberOfShare = this.onBlurNumberOfShare.bind(this);
        this.onFocusNumberOfShare = this.onFocusNumberOfShare.bind(this);
        this.onChangeAmount = this.onChangeAmount.bind(this);
        this.onBlurAmount = this.onBlurAmount.bind(this);
        this.onFocusAmount = this.onFocusAmount.bind(this);
    }

    render(): React.ReactNode {
        return (
            <div className="amount amount--stock">
                {this.renderStockForm()}
                {this.renderTarget()}
            </div>
        );
    }

    private onChangeNumberOfShare(_: undefined, value: string): void {
        const numberOfShare = formatParse.parseValueOrDefault(value, 0);
        this.setNumberOfShare(numberOfShare);
        this.setState(() => ({
            numberOfShareText: value,
            amountValueBeingUpdated: true
        } as IStockAmountState));
    }

    private onBlurNumberOfShare(): void {
        if (this.state.amountValueBeingUpdated) {
            const numberOfShare = formatParse.parseValueOrDefault(this.state.numberOfShareText, 0);
            this.setNumberOfShare(numberOfShare);
        }

        this.setState(() => ({
            isNumberOfShareOnFocus: false,
            amountValueBeingUpdated: false
        } as IStockAmountState));
    }

    private onChangeAmount(_: undefined, value: string): void {
        const amountValue = formatParse.parseValueOrDefault(value, 0);
        this.setAmount(amountValue);
        this.setState(() => ({
            amountText: value,
            amountValueBeingUpdated: true
        } as IStockAmountState));
    }

    private onBlurAmount(): void {
        if (this.state.amountValueBeingUpdated) {
            const amountValue = formatParse.parseValueOrDefault(this.state.amountText, 0);
            this.setAmount(amountValue);
        }

        this.setState(() => ({
            isAmountOnFocus: false,
            amountValueBeingUpdated: false
        } as IStockAmountState));
    }

    private onFocusNumberOfShare(): void {
        const { stockAmount, configuration } = this.props;
        const text = configuration.format.formatStockDecimal(stockAmount.numberOfShares);
        this.setState(() => ({
            isNumberOfShareOnFocus: true,
            numberOfShareText: text,
            amountValueBeingUpdated: false
        } as IStockAmountState));
    }

    private onFocusAmount(): void {
        const { stockAmount, configuration } = this.props;
        const text = configuration.format.formatCurrencyValue(
            stockAmount.currentIsoCurrencyCode,
            stockAmount.convertedMonetaryAmount ?? 0,
            false);

        this.setState(() => ({
            isAmountOnFocus: true,
            amountText: text,
            amountValueBeingUpdated: false
        } as IStockAmountState));
    }

    private renderStockForm(): React.ReactNode {
        const { stockAmount, isReadOnly } = this.props;
        if (stockAmount.isEligible === false) {
            return (
                <div className="amount-full-width-container">
                    <p className="amount-title">Stock</p>
                </div>
            );
        }

        if (isReadOnly) {
            return this.renderReadOnlyForm();
        }

        return this.renderEditableForm();
    }

    private renderEditableForm(): React.ReactNode {
        const { stockAmount } = this.props;
        return (
            <div className="input-group">
                <div className="editable-amount">
                    <div className="editable-amount__form">
                        <label htmlFor="stock-shares" className="amount-title"># of share stock</label>
                        <div className="input-label-align">
                            <FormGroup className="amount-form">
                                <Input
                                    id="stock-shares"
                                    data-mf-replace="***"
                                    value={this.numberOfShareText}
                                    onChange={this.onChangeNumberOfShare}
                                    onBlur={this.onBlurNumberOfShare}
                                    onFocus={this.onFocusNumberOfShare}
                                    className={this.getAmountClassName(AmountCode.Stock, 'shares')}
                                />
                            </FormGroup>
                        </div>
                    </div>
                    <div className="editable-amount__form" hidden={stockAmount.isValueInUnit}>
                        <label htmlFor="stock-amounts" className="amount-title">Stock</label>
                        <div className="input-label-align">
                            <FormGroup className="amount-form">
                                <Input
                                    id="stock-amounts"
                                    data-mf-replace="***"
                                    value={this.amountText}
                                    onChange={this.onChangeAmount}
                                    onBlur={this.onBlurAmount}
                                    onFocus={this.onFocusAmount}
                                    className={this.getAmountClassName(AmountCode.Stock, 'value')}
                                />
                            </FormGroup>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    private renderReadOnlyForm(): React.ReactNode {
        const { stockAmount } = this.props;
        const amountText = stockAmount.isValueInUnit ?
            this.numberOfShareText :
            `${this.amountText} / ${this.numberOfShareText}`;
        return (
            <div className="readonly-amount-form">
                <p className="amount-title">Stock</p>
                <p className="amount-readonly-text" data-mf-replace="***">
                    {amountText}
                </p>
            </div>
        );
    }

    private renderTarget(): React.ReactNode {
        const { stockAmount, configuration } = this.props;
        if (stockAmount.isEligible === false) {
            return this.renderNotEligible();
        }

        if (!stockAmount.targetAmount) {
            return null;
        }

        const targetAmountText = stockAmount.isValueInUnit
            ? `${stockAmount.targetAmount} Units`
            : configuration.format.formatCurrencyValue(
                stockAmount.currentIsoCurrencyCode,
                stockAmount.convertedTargetAmount,
                false);

        return (
            <span className="target">
                Stock Target <strong data-mf-replace="***">{targetAmountText}</strong>
            </span>
        );
    }

    private renderNotEligible(): React.ReactNode {
        const { stockAmount, configuration } = this.props;
        return (
            <EligibilityTooltip
                id={stockAmount.amountCode}
                text={configuration.getEligibilityRuleNote(stockAmount.amountCode)}
            />
        );
    }

    private get numberOfShareText(): string {
        const { stockAmount, configuration } = this.props;

        if (this.state.numberOfShareText === null) {
            return '';
        }

        if (this.state.isNumberOfShareOnFocus) {
            return this.state.numberOfShareText;
        }

        return configuration.format.formatStockDecimal(stockAmount.numberOfShares);
    }

    private get amountText(): string {
        const { stockAmount, configuration } = this.props;

        if (this.state.amountText === null) {
            return '';
        }

        if (this.state.isAmountOnFocus) {
            return this.state.amountText;
        }

        return configuration.format.formatCurrencyValue(
            stockAmount.currentIsoCurrencyCode,
            stockAmount.convertedMonetaryAmount ?? 0,
            false);
    }

    private setAmount(monetaryAmount: number): void {
        const { stockAmount, countryDecisionDispatcher } = this.props;

        const previousAmountValue = stockAmount.amount;
        stockAmount.setMonetaryAmount(monetaryAmount);
        const currentAmountValue = stockAmount.amount;
        if (!countryDecisionDispatcher) {
            return;
        }
        countryDecisionDispatcher.dispatchAmountChanged(
            AmountCode.Stock,
            stockAmount.amountIsoCurrencyCode,
            previousAmountValue,
            currentAmountValue,
            false,
            false,
            null,
            null);
    }

    private setNumberOfShare(numberOfShare: number): void {
        const { stockAmount, countryDecisionDispatcher } = this.props;

        const previousAmountValue = stockAmount.amount;
        stockAmount.setNumberOfShares(numberOfShare);
        const currentAmountValue = stockAmount.amount;
        if (!countryDecisionDispatcher) {
            return;
        }
        countryDecisionDispatcher.dispatchAmountChanged(
            AmountCode.Stock,
            stockAmount.amountIsoCurrencyCode,
            previousAmountValue,
            currentAmountValue,
            false,
            false,
            null,
            null);
    }

    private getAmountClassName(amountCode: AmountCode, modifier: string): string {
        return `amount__${amountCode}--${modifier}`;
    }
}

export default observer(StockAmount);
